import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { useMemo } from 'react';
import { selectLatestTagData } from 'store/device.slice';
import { useAppSelector } from 'store/hook';
import { selectNotificationsData } from 'store/notification.slice';
import { type AssetDetails } from 'types/squares.type';
import { processAssetsWithLiveData } from 'views/SurvivalAnalysis/Helper/Function/asset.helper.function';
import HealthScorePie from '../../DashboardV2/Helpers/HealthScore.Pie';
import { healthScorePieRange } from '../../DashboardV2/constants/squares.dashboard.constants';
import { timeAgo } from 'views/FleetOverview/components/AssetCardsV2/utils';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { PNG } from 'components/Asset/PNG';
import { ArrowCircleRightOutlined } from '@mui/icons-material';
import { type ServiceView } from 'types/survivalAnalysis.types';
import { convertAssetHealthScoreDataToServiceView } from '../utils/dataFormatters';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const compareNumbers = (numA: any, numB: any) => {
  const parsedNumA = Number(numA);
  const parsedNumB = Number(numB);
  if (isNaN(parsedNumA) && isNaN(parsedNumB)) return 0;
  if (isNaN(parsedNumA)) return 1;
  if (isNaN(parsedNumB)) return -1;

  return parsedNumB - parsedNumA;
};

export const MostRiskAssets = ({
  handleShowAssetDetails,
}: {
  handleShowAssetDetails: (
    value: boolean | null,
    paramSelectedAsset?: ServiceView,
    selectedView?: string
  ) => void;
}) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const healthData = useAppSelector(
    (state) => state.squaresDashboardReducer.healthScoreData
  );

  const latestTagData = useAppSelector(selectLatestTagData);
  const notificationsData = useAppSelector(selectNotificationsData);
  const assets = useAppSelector((state) => state.assetReducer?.assets);

  const assetWithLiveData = useMemo(() => {
    const temp = processAssetsWithLiveData(
      assets,
      latestTagData,
      notificationsData
    );
    return temp;
  }, [assets, latestTagData, notificationsData]);

  const assetHealthScoreData = useMemo(() => {
    const temp: AssetDetails[] = Object.values(healthData ?? {})
      .filter((entry: any) => entry?.asset_details)
      .map((entry: any) => entry.asset_details as AssetDetails);
    return temp;
  }, [healthData]);

  const displayedAssets: AssetDetails[] = useMemo(() => {
    const tempSorted = assetHealthScoreData
      .sort((a, b) => compareNumbers(b.health_score, a.health_score))
      .slice(0, 5);

    const tempAddLastConnected = tempSorted?.map((item) => {
      const selected = assetWithLiveData?.find(
        (el) => el?.bumperNumber === item.bumper_number
      );

      return {
        ...item,
        lastConnected: selected?.device?.ingestDate,
      };
    });

    return tempAddLastConnected;
  }, [assetHealthScoreData, assetWithLiveData]);

  const handleAssetClick = (asset: AssetDetails) => {
    const tempServiceView = convertAssetHealthScoreDataToServiceView(asset);
    handleShowAssetDetails(true, tempServiceView);
    setSearchParams({ view: 'dashboard' }, { replace: true });
  };

  const handleViewAllClick = () => {
    handleShowAssetDetails(false, undefined, 'asset_health_score');
  };

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
      }}
    >
      <Box display={'flex'} flexDirection={'column'} pt={1}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}
          alignItems={'center'}
        >
          <Typography
            sx={{ color: enableDarkTheme ? '#fff' : '#000', fontSize: 14 }}
          >
            {t('most_risk_assets')}
          </Typography>
          <Typography
            sx={{ color: enableDarkTheme ? '#fff' : '#000', fontSize: 14 }}
          >
            {`Total - ${assetHealthScoreData.length}`}
          </Typography>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
          {displayedAssets.length > 0 ? (
            displayedAssets.map((asset, index) => {
              return (
                <Box
                  key={asset.bumper_number || `asset-${index}`}
                  alignItems={'center'}
                  px={2}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '86px minmax(0, 1fr) 112px 32px',
                    columnGap: 1.5,
                    minHeight: 72,
                    backgroundColor: enableDarkTheme
                      ? palette.paperBackground
                      : palette.boxBackground,
                  }}
                >
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    sx={{
                      minWidth: 0,
                    }}
                  >
                    <Box
                      width={'72px'}
                      display={'flex'}
                      alignItems={'center'}
                      justifyContent={'center'}
                    >
                      <Box
                        height={'46px'}
                        width={'72px'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          zIndex: 1,
                        }}
                      >
                        <Box
                          component={PNG}
                          name={getImageNameByMakeModelAndAsset(
                            asset?.make,
                            asset?.model,
                            asset?.asset_type,
                            'Small'
                          )}
                          alt={asset.asset_type}
                          sx={{
                            width: '72px',
                            height: '46px',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      variant="body2"
                    >
                      {asset.bumper_number}
                    </Typography>
                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      variant="body2"
                    >
                      {asset.make} - {asset.model}
                    </Typography>

                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      variant="body2"
                      fontSize={8}
                    >
                      {asset?.lastConnected ? timeAgo(asset.lastConnected) : ''}
                    </Typography>
                  </Box>

                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    sx={{ minWidth: 0 }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <HealthScorePie
                        enableDarkTheme={enableDarkTheme}
                        data={healthScorePieRange}
                        value={Number(asset.health_score) * 100}
                        width={100}
                        height={70}
                        innerRadius={30}
                        outerRadius={40}
                        needleColor="#d0d000"
                        label={`${(Number(asset.health_score) * 100).toFixed(
                          0
                        )}%`}
                        showArrowDirection={false}
                        labelFontSize={15}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="center">
                    <IconButton
                      onClick={() => {
                        handleAssetClick(asset);
                      }}
                      size="small"
                      sx={{
                        color: palette.buttonLink,
                      }}
                    >
                      <ArrowCircleRightOutlined fontSize="medium" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '368px',
              }}
            >
              <CircularProgress />
            </div>
          )}
        </Box>
      </Box>
      <Divider
        sx={{
          background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
        }}
      />

      <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <CustomLoadingButton
          disabled={displayedAssets.length <= 0}
          variant="text"
          onClick={handleViewAllClick}
        >
          {t('view_all')}
        </CustomLoadingButton>
      </Box>
    </Box>
  );
};
